import { ComponentBase } from "@abstracts/component-base";
import {
  Component,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  AfterViewInit
} from "@angular/core";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { EnergyMonitorData } from "@dto/energyMonitorData";
import { KwhPipe } from '../../pipes/kwh.pipe';

Chart.register(...registerables);

@Component({
  selector: "energy-production-chart",
  templateUrl: "./energy-production-chart.component.html",
})
export class EnergyProductionChartComponent
  extends ComponentBase
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  public className: string = "EnergyProductionChart";

  @Input() data: EnergyMonitorData;
  @Input() date: Date;
  @Input() isLoading: boolean = true;

  public chart: Chart;
  public canvas: any;
  public ctx: any;

  public totalAmount: number;
  public max: number;
  public min: number;
  public average: number;
  public maxShow: boolean = false;
  public minShow: boolean = false;
  public isReadyForChart: boolean = false;
  public isShowingAverage: boolean = false;
  public defaultRange: string;

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit() {
    this.isReadyForChart = true
  }

  ngOnChanges(changes: any): void {
    if (!this.isReadyForChart) return;

    if (changes['data'] && this.data) {
      if (!this.chart) {
        this.createChart();
      } else {
        this.updateChart();
      }
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private initDatasets() {
    this.chart.data.datasets = [];
    const stripedPattern = this.createStripedPattern(
      `rgba(245 184 51 / .3)`,
      `rgba(240 96 36 / .3)`,
    );

    this.addDataset({
      label: ['Direktverbrauch', 'Netzeinspeisung'],

      data: [this.data.directConsumptionInWh, this.data.totalExportedInWh],

      backgroundColor: [
        `rgba(245 184 51 / .3)`,
        stripedPattern,
        // `rgba(${this.getBackgroundColor(EnergyMeterType.GRID)} / .3)`,
      ],

      borderColor: [
        `rgba(245 184 51 / .3)`,
        `rgba(240 96 36 / .3)`,
      ]
    });
  }

  private addDataset(dataset) {
    if (!dataset["cubicInterpolationMode"])
      dataset["cubicInterpolationMode"] = "monotone";
    if (!dataset["borderWidth"]) dataset["borderWidth"] = 1;
    if (!dataset["tension"]) dataset["tension"] = 0.5;

    dataset["fill"] = true;
    dataset["datalabels"] = {};
    dataset["datalabels"]["color"] = "auto";
    dataset["datalabels"]["formatter"] = (value) => {
      if (isNaN(value)) return "";
      const pipe = new KwhPipe();
      return `${pipe.transform(value, 1)} kWh`;
    };

    this.chart.data.datasets.push(dataset);
    this.chart?.update();
  }

  private createChart() {
    this.canvas = document.getElementById("energy-production-chart-canvas");
    this.ctx = this.canvas.getContext("2d");
    
    Chart.register(AnnotationPlugin);
    
    this.chart = new Chart(this.ctx, {
      plugins: [ChartDataLabels],
      type: "pie",
      data: {
        datasets: [],
      },
      options: {
        plugins: {
          datalabels: {
            color: '#000000',
            font: {
              weight: 'bolder',
              size: 14,
            },
            formatter: (value) => {
              if (isNaN(value)) return "";
              const pipe = new KwhPipe();
              return `${pipe.transform(value, 1)} kWh`;
            },
          },
          legend: {
            labels: {
              color: "#212529",
              font: {
                size: 14,
                family: "Lato",
              },
            },
          },
          tooltip: {
            enabled: false,
          },
        },
      },
    });

    this.updateChart();
  }

  private updateChart() {
    this.initDatasets();
    
    const pipe = new KwhPipe();
    this.chart.data.labels = [
      `${pipe.transform(this.data.directConsumptionInWh, 1)} kWh Direktverbrauch`,
      `${pipe.transform(this.data.totalExportedInWh, 1)} kWh Netzeinspeisung`,
    ];

    this.chart.update();
  }

  private getBackgroundColor(hex: string) {
    hex = hex.replace(/^#/, '');
    let bigint = parseInt(hex, 16);
    let r = (bigint >> 16) & 255;
    let g = (bigint >> 8) & 255;
    let b = bigint & 255;

    return `${r} ${g} ${b}`;
  }
  

  private createStripedPattern(color1: string, color2: string): CanvasPattern {
    const canvas = document.createElement('canvas');
    const size = 400; // Size of the canvas for the pattern
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
  
    if (ctx) {
      // Fill the background with color1
      ctx.fillStyle = color1;
      ctx.fillRect(0, 0, size, size);
  
      // Draw diagonal stripes with a seamless pattern
      ctx.strokeStyle = color2;
      ctx.lineWidth = 5;
  
      // Calculate the offset to ensure the stripes align perfectly
      const offset = ctx.lineWidth;
  
      // Draw stripes from top-left to bottom-right
      for (let i = -size; i < size + offset; i += ctx.lineWidth * 3) {
        ctx.beginPath();
        ctx.moveTo(i - offset, 0);
        ctx.lineTo(i + size, size);
        ctx.stroke();
      }
    }
  
    return ctx.createPattern(canvas, 'repeat')!;
  }
}
